import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Loader2, Lock, Plus, RefreshCw, Save, Search, Trash2 } from 'lucide-react';
import { apiService, PanelCatalogItem, PanelFeatureDTO, PanelGradeDTO, PanelProjectDTO, PanelUserDTO } from '@/services/api.service';
import { useAuthStore, READ_SUFFIX } from '@/stores/auth.store';
import { GAMES, supportsGameGroups } from '@/config/games';
import { useProjectPagesStore, moduleOffReason, projectModuleEnabled } from '@/stores/projectPages.store';
import { usePanelFeaturesStore } from '@/stores/panelFeatures.store';
import { useGameGroupsStore, useUsableGameGroups } from '@/stores/gameGroups.store';
import { useGameStore } from '@/stores/game.store';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { loadGradesAcrossGroups, type GradeAcrossGroups } from './gradesByGameGroup';
import { ProjectGameGroups } from '@/components/settings/ProjectGameGroups';
import { isPanelSettingsSection, isProjectSettingsSection, type PanelSettingsSection } from '@/config/settingsSections';
import { ConfigSettingsPage } from './config/ConfigSettingsPage';
// Réutilise volontairement la feuille de style d'Administration : ces sections en sont issues
// (mêmes listes, mêmes formulaires) et la scinder dupliquerait l'essentiel du SCSS.
import styles from './AdminPage.module.scss';

/**
 * Paramètres GÉNÉRAUX du panel, accessibles via l'écrou du header. Dissociés de « Grades & accès »,
 * qui ne garde que la gestion des utilisateurs et des grades : ici vivent les réglages qui valent
 * pour tout le panel (admins globaux), et ceux du projet actif venus de Configuration > Paramètres
 * (Pack, Fichiers, Containers : chacun sous son droit de grade). La garde de chaque section est
 * faite par la route (SettingsRoute, cf config/settingsSections).
 *
 * La section est portée par l'URL (/parametres/<section>), comme l'espace Statistiques : la
 * navigation vit dans la barre latérale, pas dans des onglets internes.
 */
const SECTION_HEAD: Record<PanelSettingsSection, { title: string; subtitle: string }> = {
  fonctionnalites: {
    title: 'Fonctionnalités',
    subtitle: 'Active ou coupe chaque module du panel, globalement et projet par projet.',
  },
  admins: {
    title: 'Admins',
    subtitle: "Définit qui est administrateur, au global ou d'un projet en particulier.",
  },
  grades: {
    title: 'Grades',
    subtitle: "Crée les grades du panel : pages accessibles par projet, délégation d'attribution.",
  },
  projets: {
    title: 'Projets',
    subtitle: 'Déclare les projets et leur backend satellite (URL interne, clé de signature).',
  },
  'grades-proteges': {
    title: 'Grades protégés',
    subtitle: 'Grades in-game éditables uniquement par un administrateur du panel.',
  },
};

export function GlobalSettingsPage() {
  const { section: raw } = useParams();
  // Pack, Fichiers, Containers : leur propre gabarit, pleine largeur et hauteur bornée (deux colonnes
  // qui défilent chacune). Les trois rendent le même composant ici : il reste monté de l'une à l'autre.
  if (isProjectSettingsSection(raw)) return <ConfigSettingsPage section={raw} />;
  const section: PanelSettingsSection = isPanelSettingsSection(raw) ? raw : 'fonctionnalites';
  const head = SECTION_HEAD[section];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{head.title}</h1>
        <p className={styles.subtitle}>{head.subtitle}</p>
      </header>

      {section === 'fonctionnalites' && <FeaturesSection />}
      {section === 'admins' && <AdminsSection />}
      {section === 'grades' && <GradesSection />}
      {section === 'projets' && <ProjectsSection />}
      {section === 'grades-proteges' && <ProtectedGradesSection />}
    </div>
  );
}

function timeAgo(ts: number): string {
  if (!ts) return '—';
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

// ==================== Fonctionnalités ====================

/**
 * Matrice des MODULES du panel, groupés par espace (Supervision, Configuration, Statistiques).
 *
 * Colonne « Global » = drapeau panel entier (PanelSettings) ; une colonne par projet = réglage fin,
 * pour TOUS les projets (ceux du front, toujours présents, puis ceux que le serveur connaît en plus),
 * y compris ceux sans satellite déclaré. Effectif = global ET projet (ET module parent pour une
 * section). Le catalogue (libellés, descriptions, défauts) vient du serveur.
 *
 * C'est un masquage de navigation et un garde de route côté front, pas une frontière de sécurité :
 * le vrai contrôle d'accès reste la permission de grade <projet>.<page>.
 */
function FeaturesSection() {
  const features = usePanelFeaturesStore((s) => s.features);
  const featuresLoaded = usePanelFeaturesStore((s) => s.loaded);
  const setFeaturesLocal = usePanelFeaturesStore((s) => s.setLocal);
  const fetchFeatures = usePanelFeaturesStore((s) => s.fetch);
  const pagesMap = useProjectPagesStore((s) => s.pages);
  const pagesLoaded = useProjectPagesStore((s) => s.loaded);
  const fetchPages = useProjectPagesStore((s) => s.fetch);
  const setPagesLocal = useProjectPagesStore((s) => s.setLocal);
  // ?module=<clé> : arrivée depuis l'état « Module désactivé » d'une page, la ligne est mise en avant.
  const [params] = useSearchParams();
  const focus = params.get('module');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rafraîchit TOUT au montage : un autre admin a pu changer les drapeaux ou les réglages projet
  // depuis le chargement du layout.
  useEffect(() => { void fetchFeatures(); void fetchPages(); }, [fetchFeatures, fetchPages]);

  // Espaces dans l'ordre du catalogue serveur.
  const groups = useMemo(() => {
    const map = new Map<string, PanelFeatureDTO[]>();
    features.forEach((f) => {
      if (!map.has(f.group)) map.set(f.group, []);
      map.get(f.group)!.push(f);
    });
    return [...map.entries()];
  }, [features]);

  // Colonnes : les projets du front d'abord (Paladium, NationsGlory, Recube, toujours là, même sans
  // satellite déclaré), puis ceux que seul le serveur connaît (registre des projets).
  const projects = useMemo(() => {
    const extra = Object.keys(pagesMap).filter((id) => !GAMES.some((g) => g.id === id)).sort();
    return [...GAMES.map((g) => ({ id: g.id, name: g.name })), ...extra.map((id) => ({ id, name: id }))];
  }, [pagesMap]);

  const labelOf = (key: string) => features.find((f) => f.key === key)?.label ?? key;
  const nameOf = (id: string) => GAMES.find((g) => g.id === id)?.name ?? id;

  // Ligne visée par ?module= : amenée à l'écran une fois la matrice rendue.
  useEffect(() => {
    if (!focus || features.length === 0) return;
    document.getElementById(`module-${focus}`)?.scrollIntoView({ block: 'center' });
  }, [focus, features.length]);

  const toggleGlobal = async (key: string) => {
    const flags: Record<string, boolean> = {};
    features.forEach((f) => { flags[f.key] = f.key === key ? !f.enabled : f.enabled; });
    setBusy(true);
    setError(null);
    const res = await apiService.savePanelFeatures(flags);
    setBusy(false);
    if (res.data) setFeaturesLocal(res.data.features);
    else setError(res.error ?? 'Enregistrement en échec');
  };

  const toggleProject = async (projectId: string, key: string) => {
    // Jamais sans une carte lue avec succès : la case afficherait un défaut, pas l'état en base.
    if (!pagesLoaded) return;
    // Seule la clé basculée part : le serveur garde la valeur EN BASE de toutes les autres, un
    // réglage fait entre-temps par un autre admin n'est donc pas écrasé.
    const next = { [key]: !projectModuleEnabled(pagesMap, features, projectId, key) };
    setBusy(true);
    setError(null);
    const res = await apiService.saveProjectPages(projectId, next);
    setBusy(false);
    if (res.data) setPagesLocal(projectId, res.data);
    else setError(res.error ?? 'Enregistrement en échec');
  };

  if (features.length === 0) {
    // loaded passe à vrai même sur échec (choix assumé pour la nav) : liste vide + loaded = échec
    // de chargement, pas un catalogue vide (le serveur renvoie toujours tout son catalogue).
    if (!featuresLoaded) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;
    return (
      <div className={styles.center} style={{ flexDirection: 'column', gap: 10 }}>
        <p className={styles.probeKo}>Impossible de charger les fonctionnalités.</p>
        <button className={styles.iconBtn} onClick={() => void fetchFeatures()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div>
      <p className={styles.muted} style={{ marginBottom: 12 }}>
        <strong>Global</strong> coupe un module partout d'un coup ; les colonnes par projet affinent.
        Un module n'apparaît que si les deux niveaux l'activent. Un espace dont tous les modules sont
        coupés disparaît du bandeau pour ce projet, et l'adresse d'un module coupé affiche « Module
        désactivé ». Les permissions de grade restent le contrôle d'accès : ceci ne fait que masquer.
      </p>
      {error && <p className={styles.probeKo}>{error}</p>}
      {!pagesLoaded && (
        <p className={styles.muted}>
          Réglages par projet non chargés (réseau ?) : seule la colonne Global est éditable.{' '}
          <button className={styles.iconBtn} onClick={() => void fetchPages()}>Réessayer</button>
        </p>
      )}
      {groups.map(([group, feats]) => (
        <div key={group}>
          <div className={styles.groupTitle}>{group}</div>
          <div className={styles.featureTableWrap}>
            <table className={styles.featureTable}>
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Global</th>
                  {projects.map((g) => <th key={g.id}>{g.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {feats.map((f) => (
                  <tr key={f.key} id={`module-${f.key}`}
                    className={[f.enabled ? '' : styles.featureOff, focus === f.key ? styles.featureFocus : ''].join(' ')}>
                    <td>
                      <div className={styles.featureName}>{f.label}</div>
                      {f.description && <div className={styles.featureDesc}>{f.description}</div>}
                      {(f.parent || f.defaultProjects) && (
                        <div className={styles.featureMeta}>
                          {f.parent && <>Section de « {labelOf(f.parent)} ». </>}
                          {f.defaultProjects && (
                            <>Actif par défaut sur {f.defaultProjects.length > 0
                              ? f.defaultProjects.map(nameOf).join(', ')
                              : 'aucun projet'}.</>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <input type="checkbox" checked={f.enabled} disabled={busy}
                        aria-label={`${f.label} : global`}
                        onChange={() => void toggleGlobal(f.key)} />
                    </td>
                    {projects.map((g) => {
                      // Section dont le module parent est coupé pour ce projet : réglable, mais sans effet.
                      const parentOff = f.parent ? moduleOffReason(pagesMap, features, g.id, f.parent) : null;
                      return (
                        <td key={g.id} className={parentOff ? styles.featureCellMuted : undefined}>
                          <input type="checkbox"
                            checked={projectModuleEnabled(pagesMap, features, g.id, f.key)}
                            disabled={busy || !pagesLoaded || !f.enabled}
                            aria-label={`${f.label} : ${g.name}`}
                            title={!f.enabled
                              ? 'Coupé globalement'
                              : parentOff
                                ? `Sans effet tant que « ${labelOf(parentOff.key)} » est coupé pour ${g.name}`
                                : undefined}
                            onChange={() => void toggleProject(g.id, f.key)} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== Admins ====================

/**
 * Définit qui est admin, au GLOBAL ou d'un PROJET en particulier, au niveau du user.
 *
 * S'ajoute aux deux voies existantes, affichées en badges non éditables : le bootstrap (env
 * recube.admins) et le grade admin (s'édite dans Grades & accès). Un admin de projet a toutes les
 * pages de SON projet, mais ni la gestion des grades ni les réglages globaux.
 */
function AdminsSection() {
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<PanelUserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await apiService.getPanelUsers();
    setUsers(res.data?.users ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter((u) => (u.handle ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
      : users;
    // Admins (globaux puis projet) en tête, le reste dans l'ordre d'origine (dernière connexion).
    const weight = (u: PanelUserDTO) => (u.admin ? 0 : (u.adminProjects ?? []).length > 0 ? 1 : 2);
    return [...list].sort((a, b) => weight(a) - weight(b));
  }, [users, query]);

  const save = async (u: PanelUserDTO, globalAdmin: boolean, adminProjects: string[]) => {
    setBusy(u.sub);
    setError(null);
    const res = await apiService.setPanelUserAdmin(u.sub, globalAdmin, adminProjects);
    setBusy(null);
    if (res.error) {
      setError(res.error);
      return;
    }
    void load();
  };

  const toggleGlobal = (u: PanelUserDTO) => {
    // Le backend refuse aussi : garde-fou contre l'auto-verrouillage en un clic.
    if (u.globalAdmin && u.sub === me?.xuid) {
      setError("Tu ne peux pas retirer ton propre statut d'admin global : demande à un autre admin.");
      return;
    }
    void save(u, !u.globalAdmin, u.adminProjects ?? []);
  };

  const toggleProject = (u: PanelUserDTO, projectId: string) => {
    const current = u.adminProjects ?? [];
    const next = current.includes(projectId) ? current.filter((p) => p !== projectId) : [...current, projectId];
    void save(u, !!u.globalAdmin, next);
  };

  if (loading) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;

  return (
    <div>
      <p className={styles.muted} style={{ marginBottom: 12 }}>
        <strong>Admin global</strong> = accès total au panel, gestion des grades et des réglages.
        <strong> Admin d'un projet</strong> = toutes les pages de ce projet uniquement (pas de gestion
        des grades ni des réglages globaux). L'admin hérité d'un <em>grade</em> se retire dans
        Grades &amp; accès ; celui du <em>bootstrap</em> se règle côté serveur (variable d'environnement).
      </p>
      {error && <p className={styles.probeKo}>{error}</p>}

      <div style={{ position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, opacity: 0.5 }} />
        <input
          className={styles.searchInput}
          style={{ paddingLeft: 32 }}
          placeholder="Rechercher un utilisateur (pseudo, email)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className={styles.list}>
        {visible.map((u) => (
          <div key={u.sub} className={styles.userRow}>
            <div className={styles.userAvatar}>
              {u.avatar ? <img src={u.avatar} alt={u.handle} /> : <span>{u.handle?.charAt(0).toUpperCase() ?? '?'}</span>}
            </div>
            <div className={styles.userInfo}>
              <div className={styles.userName}>
                {u.handle}
                {u.bootstrapAdmin && <span className={styles.adminTag} title="Admin d'office via la variable d'environnement recube.admins">env</span>}
                {u.gradeAdmin && <span className={styles.adminTag} title="Admin via un de ses grades (s'édite dans Grades & accès)">grade</span>}
              </div>
              <div className={styles.userMeta}>{u.email ?? '—'}</div>
            </div>
            <div className={styles.chipRow}>
              <label className={styles.adminToggle} title="Accès total + gestion complète">
                <input type="checkbox" checked={!!u.globalAdmin} disabled={busy === u.sub}
                  onChange={() => toggleGlobal(u)} />
                Admin global
              </label>
              {GAMES.map((g) => (
                <label key={g.id} className={styles.adminToggle}
                  title={u.globalAdmin ? 'Déjà admin global (couvre tous les projets)' : `Admin du projet ${g.name} uniquement`}>
                  <input type="checkbox"
                    checked={!!u.globalAdmin || (u.adminProjects ?? []).includes(g.id)}
                    disabled={busy === u.sub || !!u.globalAdmin}
                    onChange={() => toggleProject(u, g.id)} />
                  {g.name}
                </label>
              ))}
            </div>
          </div>
        ))}
        {visible.length === 0 && <p className={styles.muted}>Aucun utilisateur ne correspond.</p>}
      </div>
    </div>
  );
}

// ==================== Grades protégés ====================

/**
 * Choisir les grades in-game éditables par admin uniquement. La liste protégée est un réglage
 * GLOBAL du panel. Jeu à plusieurs groupes (NationsGlory : Nova, Iris), chacun avec son LuckPerms :
 * la liste affichée est l'union des grades de tous les groupes disponibles, chaque grade avec les
 * groupes où il existe ; un groupe illisible est signalé, sans bloquer les autres.
 */
function ProtectedGradesSection() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  // Groupes lisibles par le membre du staff : son affectation, acceptés par la passerelle.
  const usableGroups = useUsableGameGroups(activeGameId);
  const [grades, setGrades] = useState<GradeAcrossGroups[]>([]);
  const [protectedSet, setProtectedSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Union de plusieurs groupes : leurs libellés et couleurs, et ceux qui n'ont pas pu être lus.
  const [unionOf, setUnionOf] = useState<{ key: string; label: string; color?: string }[]>([]);
  const [unreadable, setUnreadable] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    // Plusieurs groupes : une requête par groupe, groupe imposé (l'union ne dépend pas du groupe
    // actif) ; sinon une lecture sur le groupe actif, comme avant.
    const res = await loadGradesAcrossGroups(supportsGameGroups(activeGameId) ? usableGroups : []);
    setGrades(res.grades);
    setProtectedSet(new Set(res.protectedGrades));
    setUnionOf(res.unionOf);
    setUnreadable(res.unreadable);
    setLoading(false);
  }, [activeGameId, usableGroups]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (name: string) => {
    const key = name.toLowerCase();
    if (key === 'admin') return; // toujours protégé, non modifiable
    const next = new Set(protectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    setProtectedSet(next);
    setSaving(true);
    await apiService.setProtectedGrades([...next].filter((n) => n !== 'admin'));
    setSaving(false);
    load();
  };

  if (loading) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;

  return (
    <div>
      <p className={styles.muted} style={{ marginBottom: 12 }}>
        Coche les grades in-game qui ne peuvent être édités (permissions, chat, boutique, apparence, suppression)
        <strong> que par un administrateur du panel</strong> — à l'image du grade <code>admin</code> de LuckPerms.
        Un staff avec l'accès aux Grades pourra les voir mais pas les modifier.
      </p>
      {unionOf.length > 1 ? (
        <p className={styles.muted} style={{ marginBottom: 12 }}>
          Grades lus dans le LuckPerms de chaque groupe ({unionOf.map((g) => g.label).join(', ')}) : la protection
          vaut pour tous les groupes.
        </p>
      ) : (
        // Un seul groupe lisible (NationsGlory) : la liste vient de son LuckPerms ; rien pour Paladium.
        <GameGroupBadge block caption="Grades lus dans le LuckPerms du groupe" />
      )}
      {unreadable.length > 0 && (
        <p className={styles.muted} style={{ marginBottom: 12 }}>
          Illisible : {unreadable.join(', ')}. Les grades qui n'existent que là n'apparaissent pas.
        </p>
      )}
      {grades.length === 0 ? (
        <p className={styles.muted}>Aucun grade in-game trouvé.</p>
      ) : (
        <div className={styles.list}>
          {grades.map((g) => {
            const key = g.name.toLowerCase();
            const always = key === 'admin';
            return (
              <label key={g.name} className={styles.pageRow}>
                <input
                  type="checkbox"
                  checked={protectedSet.has(key)}
                  disabled={always || saving}
                  onChange={() => toggle(g.name)}
                />
                <Lock size={13} style={{ opacity: protectedSet.has(key) ? 0.9 : 0.3 }} />
                {g.name}{always && <span className={styles.muted} style={{ fontSize: 12 }}> — toujours protégé</span>}
                {unionOf.length > 1 && unionOf.filter((u) => g.groups.includes(u.key)).map((u) => (
                  <GroupBadge key={u.key} group={u} title={`Grade présent dans le LuckPerms de ${u.label}`} />
                ))}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== Projets (backends satellites) ====================

/** Formulaire d'un projet. `signingKey` vide = on conserve la clé déjà enregistrée. */
interface ProjectDraft {
  id: string; name: string; color: string; baseUrl: string;
  signingKeyId: string; signingKey: string; enabled: boolean; timeoutMs: number;
  isNew: boolean;
}

function emptyProjectDraft(): ProjectDraft {
  return {
    id: '', name: '', color: '#8B3FF1', baseUrl: '',
    signingKeyId: 'default', signingKey: '', enabled: true, timeoutMs: 20000, isNew: true,
  };
}

function draftFrom(p: PanelProjectDTO): ProjectDraft {
  return {
    id: p.id, name: p.name ?? p.id, color: p.color ?? '#8B3FF1', baseUrl: p.baseUrl ?? '',
    signingKeyId: p.signingKeyId ?? 'default', signingKey: '', enabled: p.enabled,
    timeoutMs: p.timeoutMs ?? 20000, isNew: false,
  };
}

/**
 * Déclare le backend satellite de chaque projet. C'est ce qui remplace les variables
 * d'environnement du panel : brancher un projet ne demande plus de redéploiement.
 */
function ProjectsSection() {
  const [projects, setProjects] = useState<PanelProjectDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<ProjectDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ id: string; ok: boolean; message: string } | null>(null);
  const refreshGameGroups = useGameGroupsStore((s) => s.fetch);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getPanelProjects();
    setProjects(res.data?.projects ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy('save');
    const res = await apiService.savePanelProject({
      id: draft.id.trim().toLowerCase(),
      name: draft.name.trim() || draft.id,
      color: draft.color,
      baseUrl: draft.baseUrl.trim(),
      signingKeyId: draft.signingKeyId.trim() || 'default',
      // Omis quand vide : le backend conserve alors la clé existante.
      signingKey: draft.signingKey.trim() || undefined,
      enabled: draft.enabled,
      timeoutMs: draft.timeoutMs,
    });
    setBusy(null);
    if (res.error) {
      setFeedback({ id: draft.id, ok: false, message: res.error });
      return;
    }
    setDraft(null);
    setFeedback(null);
    load();
    // Projet à groupes : le sélecteur de groupe suit (racine renommée, clé changée).
    if (supportsGameGroups(draft.id.trim().toLowerCase())) void refreshGameGroups({ force: true });
  };

  const probe = async (id: string) => {
    setBusy(id);
    const res = await apiService.probePanelProject(id);
    setBusy(null);
    // Projet à groupes : la sonde relit aussi le catalogue du CICD, dont le résultat suit.
    const catalog = res.data?.catalog ? ` ${res.data.catalog.message}` : '';
    setFeedback({
      id,
      ok: !!res.data?.ok && (res.data?.catalog?.ok ?? true),
      message: (res.data?.message ?? res.error ?? 'Test impossible') + catalog,
    });
    load();
    // Santé des cibles et catalogue à jour dans le sélecteur de groupe (jeu à groupes seulement).
    if (supportsGameGroups(id)) void refreshGameGroups({ force: true });
  };

  const remove = async (p: PanelProjectDTO) => {
    if (!confirm(`Supprimer le projet « ${p.name} » ? Le panel ne pourra plus servir ses données.`)) return;
    setBusy(p.id);
    await apiService.deletePanelProject(p.id);
    setBusy(null);
    load();
  };

  if (loading) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;

  return (
    <div>
      <p className={styles.muted} style={{ marginBottom: 12 }}>
        Chaque projet est servi par un <strong>satellite</strong> déployé sur son infrastructure, qui
        détient les accès aux données de jeu. Le panel l'appelle avec une assertion signée par la clé
        déclarée ici, qui doit être <strong>identique</strong> à celle du <code>config.yaml</code> du satellite.
      </p>

      {draft ? (
        <ProjectEditor
          draft={draft}
          busy={busy === 'save'}
          error={feedback && !feedback.ok ? feedback.message : null}
          onChange={setDraft}
          onCancel={() => { setDraft(null); setFeedback(null); }}
          onSave={save}
        />
      ) : (
        <button className={styles.newBtn} onClick={() => setDraft(emptyProjectDraft())}>
          <Plus size={14} /> Déclarer un projet
        </button>
      )}

      <div className={styles.list} style={{ marginTop: 12 }}>
        {projects.length === 0 && !draft && (
          <p className={styles.muted}>Aucun projet déclaré : le panel ne peut servir aucune donnée de jeu.</p>
        )}
        {projects.map((p) => (
          <ProjectRow
            key={p.id}
            project={p}
            busy={busy === p.id}
            feedback={feedback?.id === p.id ? feedback : null}
            onEdit={() => { setDraft(draftFrom(p)); setFeedback(null); }}
            onProbe={() => probe(p.id)}
            onDelete={() => remove(p)}
            onReload={load}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectRow({ project, busy, feedback, onEdit, onProbe, onDelete, onReload }: {
  project: PanelProjectDTO;
  busy: boolean;
  feedback: { ok: boolean; message: string } | null;
  onEdit: () => void;
  onProbe: () => void;
  onDelete: () => void;
  onReload: () => void;
}) {
  const meta = project.meta;
  // Trois états distincts : jamais sondé n'est pas la même chose qu'injoignable.
  const health = !meta ? 'unknown' : meta.healthy ? 'up' : 'down';
  const healthClass = health === 'up' ? styles.healthUp : health === 'down' ? styles.healthDown : styles.healthUnknown;
  const healthLabel = health === 'up' ? 'Joignable' : health === 'down' ? 'Injoignable' : 'Jamais testé';

  const modules = meta?.modules ?? {};
  const downDatasources = Object.entries(meta?.datasources ?? {}).filter(([, v]) => v !== 'up').map(([k]) => k);

  return (
    <div className={styles.projectRow}>
      <div className={styles.projectMain}>
        <div className={styles.projectHead}>
          <span className={styles.chipDot} style={{ background: project.color ?? '#8B3FF1' }} />
          <span className={styles.projectName}>{project.name}</span>
          <code className={styles.muted} style={{ fontSize: 12 }}>{project.id}</code>
          <span className={`${styles.health} ${healthClass}`}>{healthLabel}</span>
          {!project.enabled && <span className={`${styles.health} ${styles.healthUnknown}`}>Désactivé</span>}
          {!project.hasSigningKey && <span className={`${styles.health} ${styles.healthDown}`}>Clé manquante</span>}
        </div>

        <div className={styles.projectUrl}>{project.baseUrl ?? '(aucune URL)'}</div>

        <div className={styles.projectMeta}>
          {meta?.version && <span>version {meta.version}</span>}
          <span>timeout {Math.round(project.timeoutMs / 1000)} s</span>
          {meta?.lastSeen && <span>vu {timeAgo(meta.lastSeen)}</span>}
          {meta?.error && <span className={styles.probeKo}>{meta.error}</span>}
          {downDatasources.length > 0 && (
            <span className={styles.probeKo}>bases injoignables : {downDatasources.join(', ')}</span>
          )}
        </div>

        {(meta?.capabilities?.length ?? 0) > 0 && (
          <div className={styles.capabilities}>
            {meta!.capabilities!.map((c) => <span key={c} className={styles.capability}>{c}</span>)}
            {/* Modules déclarés mais éteints : utile pour comprendre pourquoi une page est vide. */}
            {Object.entries(modules).filter(([, on]) => !on).map(([name]) => (
              <span key={name} className={`${styles.capability} ${styles.capabilityOff}`}>{name}</span>
            ))}
          </div>
        )}

        {feedback && (
          <div className={`${styles.probeResult} ${feedback.ok ? styles.probeOk : styles.probeKo}`}>
            {feedback.message}
          </div>
        )}

        {/* Groupes de jeu (Nova, Iris...) : NationsGlory seulement, jamais Paladium. */}
        {supportsGameGroups(project.id) && <ProjectGameGroups project={project} onChanged={onReload} />}
      </div>

      <div className={styles.projectActions}>
        <button className={styles.iconBtn} onClick={onProbe} disabled={busy} title="Interroger /api/_meta du satellite">
          {busy ? <Loader2 size={13} className={styles.spin} /> : <RefreshCw size={13} />} Tester
        </button>
        <button className={styles.iconBtn} onClick={onEdit} disabled={busy}>Éditer</button>
        <button className={styles.iconBtn} onClick={onDelete} disabled={busy} title="Retirer du registre">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function ProjectEditor({ draft, busy, error, onChange, onCancel, onSave }: {
  draft: ProjectDraft;
  busy: boolean;
  error: string | null;
  onChange: (d: ProjectDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const set = <K extends keyof ProjectDraft>(key: K, value: ProjectDraft[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <div className={styles.gradeEditor}>
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label>Identifiant</label>
          <input
            className={styles.input}
            value={draft.id}
            disabled={!draft.isNew}
            placeholder="nationsglory"
            onChange={(e) => set('id', e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Nom affiché</label>
          <input className={styles.input} value={draft.name} placeholder="NationsGlory"
                 onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Couleur</label>
          <input className={styles.input} type="color" value={draft.color}
                 onChange={(e) => set('color', e.target.value)} style={{ width: 56, padding: 2 }} />
        </div>
      </div>

      <div className={styles.field}>
        <label>URL interne du satellite</label>
        <input className={styles.input} value={draft.baseUrl} placeholder="http://172.27.100.1:3030"
               onChange={(e) => set('baseUrl', e.target.value)} />
        <span className={styles.muted} style={{ fontSize: 11 }}>
          Adresse privée uniquement (10.x, 172.16-31.x, 192.168.x, localhost ou *.internal).
        </span>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label>Identifiant de clé (kid)</label>
          <input className={styles.input} value={draft.signingKeyId} placeholder="ng-2026-08"
                 onChange={(e) => set('signingKeyId', e.target.value)} />
        </div>
        <div className={styles.field} style={{ flex: 1, minWidth: 260 }}>
          <label>Clé de signature</label>
          <input
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={draft.signingKey}
            placeholder={draft.isNew ? 'secret partagé avec le satellite' : '•••••••• (inchangée)'}
            onChange={(e) => set('signingKey', e.target.value)}
          />
          <span className={styles.muted} style={{ fontSize: 11 }}>
            Jamais relue par l'API. Laisser vide pour conserver la clé actuelle.
          </span>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label>Timeout (ms)</label>
          <input className={styles.input} type="number" min={1000} step={1000} value={draft.timeoutMs}
                 onChange={(e) => set('timeoutMs', Number(e.target.value) || 20000)} style={{ width: 120 }} />
        </div>
        <label className={styles.adminToggle}>
          <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          Actif
        </label>
      </div>

      {error && <div className={`${styles.probeResult} ${styles.probeKo}`}>{error}</div>}

      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={onSave} disabled={busy || !draft.id.trim() || !draft.baseUrl.trim()}>
          {busy ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
        </button>
        <button className={styles.iconBtn} onClick={onCancel} disabled={busy}>Annuler</button>
      </div>
    </div>
  );
}

// ==================== Grades (rôles du panel) ====================

interface Catalog { projects: PanelCatalogItem[]; pages: PanelCatalogItem[]; }

/** Niveau d'accès d'un grade à une page. */
type PageLevel = 'none' | 'read' | 'write';

const LEVELS: { value: PageLevel; label: string }[] = [
  { value: 'none', label: 'Aucun' },
  { value: 'read', label: 'Lecture' },
  { value: 'write', label: 'Écriture' },
];

interface Draft {
  id?: string; name: string; color: string; admin: boolean; permissions: Set<string>;
  manageAccess: boolean; canAssignGrades: boolean; assignableGradeIds: Set<string>;
}

/**
 * Création/édition des grades du panel. Déplacé depuis « Grades & accès », qui ne garde que
 * l'ATTRIBUTION aux utilisateurs : créer un grade est un réglage général (et un vecteur
 * d'escalade), il vit donc ici, derrière l'écrou, admins uniquement.
 */
function GradesSection() {
  const [grades, setGrades] = useState<PanelGradeDTO[]>([]);
  const [catalog, setCatalog] = useState<Catalog>({ projects: [], pages: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [g, c] = await Promise.all([apiService.getPanelGrades(), apiService.getPanelCatalog()]);
    setGrades(g.data?.grades ?? []);
    if (c.data) setCatalog(c.data);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (loading) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;
  return <RolesEditor grades={grades} catalog={catalog} onReload={load} />;
}

function RolesEditor({ grades, catalog, onReload }: { grades: PanelGradeDTO[]; catalog: Catalog; onReload: () => void }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [project, setProject] = useState('');
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Réordonnancement « à la Discord » : on déplace un grade parmi ses pairs. Les grades admin restent
  // forcément au-dessus des non-admins → on interdit de franchir la frontière admin/non-admin.
  const canUp = (i: number) => i > 0 && grades[i - 1]!.admin === grades[i]!.admin;
  const canDown = (i: number) => i < grades.length - 1 && grades[i + 1]!.admin === grades[i]!.admin;

  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= grades.length) return;
    if (grades[j]!.admin !== grades[i]!.admin) return;
    const order = grades.map((g) => g.id);
    [order[i], order[j]] = [order[j]!, order[i]!];
    setReordering(true);
    await apiService.reorderPanelGrades(order);
    setReordering(false);
    onReload();
  };

  useEffect(() => {
    if (!project && catalog.projects.length > 0) setProject(catalog.projects[0]!.key);
  }, [catalog, project]);

  const edit = (g: PanelGradeDTO) => setDraft({
    id: g.id, name: g.name, color: g.color, admin: g.admin, permissions: new Set(g.permissions),
    manageAccess: !!g.manageAccess, canAssignGrades: !!g.canAssignGrades,
    assignableGradeIds: new Set(g.assignableGradeIds ?? []),
  });
  const create = () => setDraft({
    name: '', color: '#8B3FF1', admin: false, permissions: new Set(),
    manageAccess: false, canAssignGrades: false, assignableGradeIds: new Set(),
  });

  // Pages regroupées par famille (ordre du catalogue préservé).
  const families = useMemo(() => {
    const map = new Map<string, PanelCatalogItem[]>();
    catalog.pages.forEach((p) => {
      const fam = p.group ?? 'Autres';
      if (!map.has(fam)) map.set(fam, []);
      map.get(fam)!.push(p);
    });
    return [...map.entries()];
  }, [catalog.pages]);

  /** Niveau d'accès d'un grade à une page : rien, lecture seule, ou écriture (lecture incluse). */
  const levelOf = (pageKey: string): PageLevel => {
    if (!draft) return 'none';
    if (draft.permissions.has(`${project}.${pageKey}`)) return 'write';
    if (draft.permissions.has(`${project}.${pageKey}${READ_SUFFIX}`)) return 'read';
    return 'none';
  };

  /** Pose le niveau d'une page : les deux clés sont exclusives, on retire toujours l'autre. */
  const applyLevel = (perms: Set<string>, pageKey: string, level: PageLevel) => {
    perms.delete(`${project}.${pageKey}`);
    perms.delete(`${project}.${pageKey}${READ_SUFFIX}`);
    if (level === 'write') perms.add(`${project}.${pageKey}`);
    else if (level === 'read') perms.add(`${project}.${pageKey}${READ_SUFFIX}`);
  };

  const setPageLevel = (pageKey: string, level: PageLevel) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    applyLevel(next, pageKey, level);
    setDraft({ ...draft, permissions: next });
  };

  const setBulkLevel = (pages: PanelCatalogItem[], level: PageLevel) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    pages.forEach((p) => applyLevel(next, p.key, level));
    setDraft({ ...draft, permissions: next });
  };

  const toggleAssignable = (gradeId: string) => {
    if (!draft) return;
    const next = new Set(draft.assignableGradeIds);
    if (next.has(gradeId)) next.delete(gradeId); else next.add(gradeId);
    setDraft({ ...draft, assignableGradeIds: next });
  };

  const save = async () => {
    if (!draft || !draft.name.trim()) return;
    setSaving(true);
    await apiService.savePanelGrade({
      id: draft.id, name: draft.name.trim(), color: draft.color, admin: draft.admin,
      permissions: [...draft.permissions],
      manageAccess: draft.manageAccess, canAssignGrades: draft.canAssignGrades,
      assignableGradeIds: [...draft.assignableGradeIds],
    });
    setSaving(false);
    setDraft(null);
    onReload();
  };

  const remove = async () => {
    if (!draft?.id) { setDraft(null); return; }
    if (!window.confirm(`Supprimer le grade « ${draft.name} » ?`)) return;
    setSaving(true);
    await apiService.deletePanelGrade(draft.id);
    setSaving(false);
    setDraft(null);
    onReload();
  };

  return (
    <div className={styles.rolesLayout}>
      <div className={styles.gradeList}>
        <button className={styles.newBtn} onClick={create}><Plus size={14} /> Nouveau grade</button>
        {grades.map((g, i) => (
          <div key={g.id} className={styles.gradeItemRow}>
            <button
              className={`${styles.gradeItem} ${draft?.id === g.id ? styles.gradeItemActive : ''}`}
              style={{ flex: 1 }}
              onClick={() => edit(g)}
            >
              <span className={styles.gradeDot} style={{ background: g.color }} />
              {g.name}{g.admin && <span className={styles.adminTag}>admin</span>}
            </button>
            <div className={styles.gradeMove}>
              <button className={styles.moveBtn} title="Monter" disabled={reordering || !canUp(i)} onClick={() => move(i, -1)}>
                <ChevronUp size={12} />
              </button>
              <button className={styles.moveBtn} title="Descendre" disabled={reordering || !canDown(i)} onClick={() => move(i, 1)}>
                <ChevronDown size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.gradeEditor}>
        {!draft ? (
          <p className={styles.muted}>Sélectionne un grade à éditer ou crée-en un.</p>
        ) : (
          <>
            <div className={styles.field}>
              <label>Nom</label>
              <input className={styles.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="ex. Modérateur" />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label>Couleur</label>
                <input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
              </div>
              <label className={styles.adminToggle}>
                <input type="checkbox" checked={draft.admin} onChange={(e) => setDraft({ ...draft, admin: e.target.checked })} />
                Admin (accès total + gestion complète users/grades)
              </label>
            </div>

            {!draft.admin && (
              <>
                <div className={styles.delegation}>
                  <span className={styles.delegationTitle}>Gestion des accès</span>
                  <label className={styles.adminToggle}>
                    <input
                      type="checkbox"
                      checked={draft.manageAccess}
                      onChange={(e) => setDraft({ ...draft, manageAccess: e.target.checked })}
                    />
                    Peut accéder à « Grades &amp; accès » (voir les utilisateurs et grades)
                  </label>
                  <label className={styles.adminToggle}>
                    <input
                      type="checkbox"
                      checked={draft.canAssignGrades}
                      onChange={(e) => setDraft({ ...draft, canAssignGrades: e.target.checked })}
                    />
                    Peut attribuer des grades aux utilisateurs
                  </label>
                  {draft.canAssignGrades && (
                    <div className={styles.assignList}>
                      <span className={styles.muted} style={{ fontSize: 12 }}>Grades qu'il peut attribuer :</span>
                      {grades.filter((g) => g.id !== draft.id).length === 0 && (
                        <span className={styles.muted} style={{ fontSize: 12 }}>Aucun autre grade à attribuer.</span>
                      )}
                      {grades.filter((g) => g.id !== draft.id).map((g) => (
                        <label key={g.id} className={styles.pageRow}>
                          <input
                            type="checkbox"
                            checked={draft.assignableGradeIds.has(g.id)}
                            onChange={() => toggleAssignable(g.id)}
                          />
                          <span className={styles.chipDot} style={{ background: g.color }} />
                          {g.name}{g.admin ? ' (admin)' : ''}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.matrix}>
                  <div className={styles.projectTabs}>
                    {catalog.projects.map((p) => (
                      <button key={p.key} className={`${styles.projectTab} ${project === p.key ? styles.projectTabActive : ''}`} onClick={() => setProject(p.key)}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className={styles.muted} style={{ fontSize: 12, margin: '4px 0 8px' }}>
                    <strong>Lecture</strong> ouvre la page sans autoriser la moindre modification.
                    <strong> Écriture</strong> inclut la lecture. « Publications » en écriture est
                    le droit de <em>publier sans relecture</em> (le bouton Envoyer publie tout de suite)
                    et de valider les demandes des autres : à ne pas confondre avec celui d'éditer le
                    contenu, qui ne fait qu'envoyer des demandes.
                  </p>

                  <div className={styles.bulkLevels}>
                    <span className={styles.muted}>Tout {catalog.projects.find((p) => p.key === project)?.label} :</span>
                    {LEVELS.map((l) => (
                      <button key={l.value} type="button" className={styles.levelBtn}
                        onClick={() => setBulkLevel(catalog.pages, l.value)}>
                        {l.label}
                      </button>
                    ))}
                  </div>

                  {families.map(([fam, pages]) => (
                    <div key={fam}>
                      <div className={styles.familyHeader}>
                        {fam}
                        <span className={styles.familyAll}>
                          {LEVELS.map((l) => (
                            <button key={l.value} type="button" className={styles.levelBtn}
                              onClick={() => setBulkLevel(pages, l.value)}>
                              {l.label}
                            </button>
                          ))}
                        </span>
                      </div>
                      {pages.map((p) => {
                        const level = levelOf(p.key);
                        return (
                          <div key={p.key} className={styles.pageRow}>
                            <span className={styles.pageLabel}>{p.label}</span>
                            <span className={styles.levelGroup}>
                              {LEVELS.map((l) => (
                                <button
                                  key={l.value}
                                  type="button"
                                  className={`${styles.levelBtn} ${level === l.value ? styles.levelBtnActive : ''}`}
                                  onClick={() => setPageLevel(p.key, l.value)}
                                >
                                  {l.label}
                                </button>
                              ))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={styles.actions}>
              <button className={styles.saveBtn} onClick={save} disabled={saving || !draft.name.trim()}>
                {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
              </button>
              <button className={styles.deleteBtn} onClick={remove} disabled={saving}>
                <Trash2 size={14} /> {draft.id ? 'Supprimer' : 'Annuler'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
